import React, { createContext, useContext, useEffect, useState, startTransition } from 'react';
import type { User } from 'firebase/auth';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  isAdmin: boolean;
  sessionInfo: any;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true,
  isAdmin: false,
  sessionInfo: null
});

export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [sessionInfo, setSessionInfo] = useState<any>(null);

  const loadEnhancedAuthService = async () => {
    const mod = await import('@/lib/enhancedAuthService');
    return mod.enhancedAuthService;
  };

  useEffect(() => {
    // Add global error handler for unhandled promise rejections
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      // Handle null/undefined reasons safely
      if (event.reason === null || event.reason === undefined) {
        console.warn('[AuthProvider] Unhandled promise rejection: null');
        event.preventDefault();
        return;
      }
      
      const errorMessage = event.reason?.message || String(event.reason);
      
      // Check if this is a "No response received" error
      if (errorMessage.includes('No response received') || 
          errorMessage.includes('Token retrieval timeout') ||
          errorMessage.includes('network-request-failed')) {
        console.warn('[AuthProvider] Network/timeout error caught:', errorMessage);
        
        // Try to recover authentication silently
        loadEnhancedAuthService()
          .then(service => service.recoverAuthentication())
          .catch(recoveryError => {
            console.error('[AuthProvider] Recovery failed:', recoveryError);
          });
      } else {
        console.error('[AuthProvider] Unhandled promise rejection:', event.reason);
      }
      
      // Prevent the error from being logged to console as unhandled
      event.preventDefault();
    };
    
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    
    return () => {
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  useEffect(() => {
    // Always initialize auth listener for admin routes to ensure session persistence
    const isAdminRoute = window.location.pathname.startsWith('/admin');
    
    // Improved Firebase auth detection - check for any Firebase-related keys
    const hasStoredAuth = (() => {
      try {
        // Check for any Firebase auth-related keys in localStorage
        const allKeys = Object.keys(localStorage);
        const firebaseAuthKeys = allKeys.filter(key => 
          key.includes('firebase:authUser:') || 
          key.includes('firebaseLocalStorageDb') ||
          (key.startsWith('firebase:') && key.includes('authUser'))
        );
        
        // Check if any of these keys have valid (non-null) values
        return firebaseAuthKeys.some(key => {
          const stored = localStorage.getItem(key);
          return stored && stored !== 'null' && stored !== 'undefined' && stored.trim() !== '';
        });
      } catch (error) {
        console.warn('[AuthProvider] Error checking localStorage:', error);
        return false;
      }
    })();
    
    // For admin routes, ALWAYS initialize Firebase Auth regardless of localStorage
    // For non-admin routes, only skip if there's definitely no stored auth
    if (!isAdminRoute && !hasStoredAuth) {
      console.log('[AuthProvider] Skipping Firebase Auth initialization for public page without stored auth');
      setUser(null);
      setIsAdmin(false);
      setSessionInfo(null);
      setLoading(false);
      return;
    }

    // Initialize Firebase Auth for admin routes or when there's stored auth
    console.log('[AuthProvider] Initializing Firebase Auth:', isAdminRoute ? 'admin route' : 'stored auth detected');
    
    let cancelled = false;
    let unsubscribe: null | (() => void) = null;
    let loadingTimeout: number | null = null;

    (async () => {
      try {
        const enhancedAuthService = await loadEnhancedAuthService();
        if (cancelled) return;

        console.log(
          '[AuthProvider] Initializing enhanced auth state monitoring for',
          isAdminRoute ? 'admin route' : 'authenticated user'
        );

        unsubscribe = enhancedAuthService.onAuthStateChanged((nextUser) => {
          console.log('[AuthProvider] Auth state changed:', nextUser ? nextUser.uid : 'signed out');
          console.log('[AuthProvider] User object:', nextUser);

          // Wrap state updates in startTransition to prevent suspension
          startTransition(() => {
            setUser(nextUser);

            // Update admin status and session info
            const session = enhancedAuthService.getSessionInfo();
            console.log('[AuthProvider] Session info:', session);
            console.log('[AuthProvider] Is admin from session:', session?.isAdmin);
            setIsAdmin(session?.isAdmin || false);
            setSessionInfo(session);

            // Always stop loading when auth state changes
            setLoading(false);
            console.log(
              '[AuthProvider] Auth state updated - loading:',
              false,
              'isAdmin:',
              session?.isAdmin || false
            );
          });
        });

        // For admin routes, explicitly check and restore auth state first
        if (isAdminRoute) {
          enhancedAuthService
            .checkAndRestoreAuthState()
            .then((restored) => {
              console.log(
                '[AuthProvider] Admin auth state restoration:',
                restored ? 'successful' : 'failed'
              );
              if (!restored) {
                startTransition(() => {
                  setLoading(false);
                });
              }
            })
            .catch((error) => {
              console.error('[AuthProvider] Admin auth state restoration error:', error);
              startTransition(() => {
                setLoading(false);
              });
            });
        }

        // Fallback timeout to prevent infinite loading
        loadingTimeout = window.setTimeout(() => {
          console.warn('[AuthProvider] Loading timeout reached, stopping loading state');
          startTransition(() => {
            setLoading(false);
          });
        }, 5000);
      } catch (error) {
        console.error('[AuthProvider] Enhanced auth initialization failed:', error);
        setUser(null);
        setIsAdmin(false);
        setSessionInfo(null);
        setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      if (loadingTimeout) window.clearTimeout(loadingTimeout);
      if (unsubscribe) unsubscribe();
    };
  }, []);

  const value = {
    user,
    loading,
    isAdmin,
    sessionInfo
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
